/**
 * BoxInfoOverlay — AR overlay that displays box name and contents
 * floating near detected markers on the camera view.
 *
 * Uses CSS positioned overlays anchored to marker center points.
 */

import { useMemo } from 'react';
import { createCoordinateMapper } from './coordinateMapper';
import type { TrackedMarker } from '../detection/types';

export interface BoxInfoOverlayProps {
  trackedMarkers: TrackedMarker[];
  videoWidth: number;
  videoHeight: number;
  detectionWidth: number;
  detectionHeight: number;
  className?: string;
}

export function BoxInfoOverlay({
  trackedMarkers,
  videoWidth,
  videoHeight,
  detectionWidth,
  detectionHeight,
  className = '',
}: BoxInfoOverlayProps) {
  // Only show markers that have resolved box data
  const markersWithBoxes = useMemo(() => trackedMarkers.filter((m) => m.box), [trackedMarkers]);

  const mapper = useMemo(
    () =>
      createCoordinateMapper({
        videoWidth,
        videoHeight,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        detectionWidth,
        detectionHeight,
      }),
    [videoWidth, videoHeight, detectionWidth, detectionHeight],
  );

  if (markersWithBoxes.length === 0) return null;

  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      {markersWithBoxes.map((marker) => {
        const box = marker.box!;

        // Calculate marker center point
        const cx = marker.corners.reduce((sum, c) => sum + c.x, 0) / marker.corners.length;
        const cy = marker.corners.reduce((sum, c) => sum + c.y, 0) / marker.corners.length;

        // Map to screen coordinates
        const screenPos = mapper.toScreen({ x: cx, y: cy });

        // Offset below the marker
        const top = screenPos.y + 20;
        const left = screenPos.x;

        return (
          <div
            key={`${marker.markerType}:${marker.markerId}`}
            className="absolute"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translateX(-50%)',
              maxWidth: '280px',
            }}
          >
            <div className="bg-black/80 backdrop-blur-sm text-white rounded-lg p-3 shadow-xl border border-accent/30">
              {/* Box name */}
              <div className="font-semibold text-sm mb-1.5 text-accent">{box.name}</div>

              {/* Location */}
              {box.location && <div className="text-xs text-gray-300 mb-2">📍 {box.location}</div>}

              {/* Item count */}
              {box.items.length > 0 ? (
                <div className="text-sm text-gray-300">
                  📦 {box.items.length} {box.items.length === 1 ? 'item' : 'items'}
                </div>
              ) : (
                <div className="text-xs text-gray-400 italic">Empty box</div>
              )}

              {/* Marker ID badge */}
              <div className="text-[10px] text-gray-500 mt-2 border-t border-gray-700 pt-1.5">
                {marker.markerType === 'aruco' ? 'ArUco' : 'QR'} #{marker.markerId}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
